import { getRoleDisplayLabel, sortRolesByDisplayOrder } from './specialistRoleModuleHelper';

export const toggleNumericId = (list, id) => {
  const value = Number(id);
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
};

export const getGuideRoleId = (role = {}) => Number(role.catalogrole_id || role.id);

export const getGuideModuleId = (moduleItem = {}) => Number(moduleItem.catalogmodule_id || moduleItem.id);

export const formatGuideBytes = (bytes) => {
  const size = Number(bytes || 0);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export const GUIDE_PHASE_LABELS = {
  FASE_1_PROGRAMACION_PREVIA: 'FASE 1 PROGRAMACION_PREVIA',
  FASE_2_EXPLORACION: 'FASE 2 EXPLORACION',
  FASE_3_RELEVAMIENTO: 'FASE 3 RELEVAMIENTO',
  FASE_4_LIMPIEZA_DATOS: 'FASE 4 LIMPIEZA_DATOS',
  FASE_5_EDICION: 'FASE 5 EDICION',
  FASE_6_ACTUALIZACION: 'FASE 6 ACTUALIZACION'
};

export const GUIDE_PHASE_ORDER = [
  'FASE_1_PROGRAMACION_PREVIA',
  'FASE_2_EXPLORACION',
  'FASE_3_RELEVAMIENTO',
  'FASE_4_LIMPIEZA_DATOS',
  'FASE_5_EDICION',
  'FASE_6_ACTUALIZACION'
];

export const formatGuidePhaseLabel = (phase) => {
  const key = String(phase || '').trim();
  if (!key) return '';
  if (GUIDE_PHASE_LABELS[key]) return GUIDE_PHASE_LABELS[key];
  return key.replace(/_/g, ' ');
};

export const getGuidePhase = (guide = {}) => String(guide.guide_phase || '').trim();

export const getGuideRoleLabel = (role, t) => {
  if (!role) return '';
  const translated = role.name ? getRoleDisplayLabel(role.name, t) : '';
  if (translated && translated !== role.name) {
    return translated;
  }
  return role.description || translated || role.name || '';
};

export const guideMatchesRole = (guide, roleId) => {
  if (!roleId) return true;
  const target = Number(roleId);
  return (guide.roles || []).some((item) => getGuideRoleId(item) === target);
};

export const guideMatchesModules = (guide, moduleIds) => {
  if (!moduleIds || !moduleIds.length) return true;
  if (guide.applies_to_all_modules) return true;
  const assigned = new Set((guide.modules || []).map(getGuideModuleId));
  return moduleIds.some((id) => assigned.has(Number(id)));
};

export const guideMatchesPhase = (guide, phase) => {
  if (!phase) return true;
  return getGuidePhase(guide) === String(phase).trim();
};

export const listGuidePhases = (available = GUIDE_PHASE_ORDER) => {
  const source = Array.isArray(available) && available.length ? available : GUIDE_PHASE_ORDER;
  return source
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .map((value) => ({
      value,
      label: formatGuidePhaseLabel(value)
    }));
};

export const countVisibleGuides = (guides = []) => {
  const ids = new Set((guides || []).map((row) => Number(row.id)).filter((id) => Number.isFinite(id) && id > 0));
  return ids.size;
};

export const listUserGuideRoles = (optionsRoles = [], userRoleState = {}) => {
  const userNames = new Set(
    (userRoleState?.roleLogin?.roles || [])
      .map((row) => row?.CatalogRole_To_UserRole?.name || row?.name || '')
      .filter(Boolean)
  );
  const matched = (optionsRoles || []).filter((role) => userNames.has(role.name));
  return sortRolesByDisplayOrder(matched.length ? matched : optionsRoles);
};

export const groupGuidesByRole = (guides = [], catalogRoles = []) => {
  const buckets = new Map();
  (catalogRoles || []).forEach((role) => {
    buckets.set(Number(role.id), { role, guides: [] });
  });

  (guides || []).forEach((guide) => {
    (guide.roles || []).forEach((role) => {
      const roleId = getGuideRoleId(role);
      if (!buckets.has(roleId)) {
        buckets.set(roleId, { role, guides: [] });
      }
      buckets.get(roleId).guides.push(guide);
    });
  });

  return sortRolesByDisplayOrder(
    [...buckets.values()].map((bucket) => bucket.role)
  )
    .map((role) => buckets.get(getGuideRoleId(role)))
    .filter((bucket) => bucket && bucket.guides.length);
};

export const groupGuidesByRoleAndPhase = (guides = [], catalogRoles = []) => {
  const roleIndex = new Map();
  (catalogRoles || []).forEach((role) => {
    roleIndex.set(Number(role.id), role);
  });

  const buckets = new Map();
  (guides || []).forEach((guide) => {
    const phase = getGuidePhase(guide);
    const roles = (guide.roles || []).length ? guide.roles : [{ id: 0, description: '' }];
    roles.forEach((role) => {
      const roleId = getGuideRoleId(role);
      const resolvedRole = roleIndex.get(roleId) || role;
      const key = `${roleId}::${phase}`;
      if (!buckets.has(key)) {
        buckets.set(key, { role: resolvedRole, phase, guides: [] });
      }
      buckets.get(key).guides.push(guide);
    });
  });

  const phaseRank = (phase) => {
    const idx = GUIDE_PHASE_ORDER.indexOf(phase);
    return idx === -1 ? GUIDE_PHASE_ORDER.length : idx;
  };

  return [...buckets.values()].sort((a, b) => {
    const phaseDiff = phaseRank(a.phase) - phaseRank(b.phase);
    if (phaseDiff !== 0) return phaseDiff;
    const roleA = getGuideRoleId(a.role);
    const roleB = getGuideRoleId(b.role);
    return roleA - roleB;
  });
};
